import React, { useCallback, useEffect, useRef } from 'react';
import clsx from 'clsx';
import PropTypes from 'prop-types';
import { Formik } from 'formik';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  Typography,
  TextField,
  IconButton,
  FormControl,
  FormHelperText,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useTranslation } from 'react-i18next';
import { getDifferenceBetweenStates } from 'src/utils/common';
import DialogTransition from 'src/components/DialogTransition';
import PhoneInput from 'src/components/PhoneInput';
import DropdownMenu from 'src/components/DropdownMenu';
import { UserRoles } from 'src/utils/constants/auth';
import makeInitialState from './initialState';
import validationSchema from './schema';
import useStyles from './styles';

const AdministratorDialog = ({
  open,
  handleClose,
  onSubmit,
  administrator,
  isEmailValidated = false
}) => {
  const { classes } = useStyles();
  const initialState = makeInitialState(administrator);
  const resetRef = useRef();
  const { t } = useTranslation();

  const USER_ROLES_LIST = [
    { name: t('NAVIGATION.ADMINISTRATOR'), key: UserRoles.ADMIN },
    { name: t('NAVIGATION.AUDITOR'), key: UserRoles.AUDITOR }
  ];

  const handleOuterSubmit = useCallback(
    (values, { resetForm }) => {
      if (administrator) {
        const difference = getDifferenceBetweenStates(initialState, values);
        if (Object.entries(difference).length === 0) {
          handleClose();
          return;
        }
        onSubmit({
          ...difference
        });
      } else {
        onSubmit({
          ...values
        });
      }
      resetForm(initialState);
      handleClose();
    },
    [onSubmit, handleClose, initialState]
  );

  useEffect(() => {
    return () => {
      if (resetRef.current) {
        resetRef.current();
      }
    };
  }, [resetRef, open]);

  return (
    <Dialog
      disableBackdropClick
      disableEscapeKeyDown
      classes={{ paper: classes.dialogRoot }}
      open={open}
      TransitionComponent={DialogTransition}
      keepMounted
    >
      <DialogTitle className={classes.header} id="form-general-dialog-title">
        <IconButton
          key="close"
          aria-label="Close"
          color="inherit"
          onClick={() => {
            handleClose();
          }}
          className={classes.closeIcon}
        >
          <CloseIcon />
        </IconButton>
        <Typography className={classes.header} color="primary" variant="h2">
          {administrator
            ? t('ADMINISTRATORS_VIEW.EDIT_ADMINISTRATOR')
            : t('ADMINISTRATORS_VIEW.CREATE_ADMINISTRATOR')}
        </Typography>
      </DialogTitle>
      <DialogContent style={{ marginBottom: '16px' }}>
        <Formik
          initialValues={initialState}
          validationSchema={validationSchema}
          onSubmit={handleOuterSubmit}
          enableReinitialize
        >
          {({
            errors,
            handleBlur,
            handleChange,
            handleSubmit,
            touched,
            values,
            resetForm,
            setFieldValue
          }) => {
            resetRef.current = () => resetForm(initialState);
            return (
              <form onSubmit={handleSubmit}>
                <TextField
                  InputProps={{
                    autoComplete: 'off'
                  }}
                  error={Boolean(touched.name && errors.name)}
                  fullWidth
                  helperText={touched.name && t(errors.name)}
                  label={t('COMMON.NAME')}
                  margin="normal"
                  name="name"
                  onBlur={handleBlur}
                  onChange={handleChange}
                  type="text"
                  value={values.name}
                  variant="outlined"
                />
                <DropdownMenu
                  list={USER_ROLES_LIST}
                  value={values.role}
                  setValue={value => setFieldValue('role', value)}
                  label={t('COMMON.ROLE')}
                  error={Boolean(touched.role && errors.role)}
                  errorText={touched.role && t(errors.role)}
                  disabled={!!administrator}
                />

                <FormControl fullWidth margin="normal">
                  <PhoneInput
                    id="homePhone-label"
                    name="phone"
                    label={t('COMMON.PHONE')}
                    value={values.phone}
                    onBlur={handleBlur}
                    onChange={setFieldValue}
                    error={Boolean(touched.phone && errors.phone)}
                  />
                  {Boolean(touched.phone && errors.phone) && (
                    <FormHelperText sx={{ color: '#d32f2f' }}>
                      {t(errors.phone)}
                    </FormHelperText>
                  )}
                </FormControl>

                <Tooltip
                  title={
                    isEmailValidated
                      ? t('DISTRIBUTORS_VIEW.AGENTS.EMAIL_VALIDATED_MESSAGE')
                      : ''
                  }
                >
                  <TextField
                    InputProps={{
                      autoComplete: 'off'
                    }}
                    error={Boolean(touched.email && errors.email)}
                    fullWidth
                    helperText={touched.email && t(errors.email)}
                    label={t('COMMON.EMAIL')}
                    margin="normal"
                    name="email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    type="text"
                    disabled={isEmailValidated}
                    value={values.email}
                    variant="outlined"
                  />
                </Tooltip>

                <Grid container direction="column" alignItems="stretch">
                  <Grid item className={classes.buttons}>
                    <Button
                      className={clsx(classes.label, classes.buttons)}
                      type="submit"
                      color="primary"
                      variant="outlined"
                    >
                      {administrator ? t('COMMON.EDIT') : t('COMMON.CREATE')}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

AdministratorDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  administrator: PropTypes.object,
  isEmailValidated: PropTypes.bool
};

export default AdministratorDialog;
